import { useState, useEffect } from 'react';
import {
  Modal,
  Spinner,
  SpinnerSize
} from '@fluentui/react';
import { 
  DocumentRegular, 
  ArrowSyncRegular,
  ArrowUploadRegular,
  CheckmarkCircleRegular,
  DismissRegular,
  ClockRegular
} from '@fluentui/react-icons';
import styles from './DuplicateFileModal.module.css';

export interface DuplicateFileInfo {
  filename: string;
  existingFiles: {
    id: string;
    filename: string;
    filetype: string;
    upload_date: string;
    content_preview: string;
    content_length: number;
    processing_status: string;
  }[];
}

export interface DuplicateCheckResult {
  filename: string;
  file: File;
  duplicates: DuplicateFileInfo['existingFiles'];
  action?: 'reuse' | 'reupload' | 'skip';
  selectedExistingId?: string;
}

interface Props {
  isOpen: boolean;
  duplicateFiles: DuplicateCheckResult[];
  onClose: () => void;
  onConfirm: (decisions: DuplicateCheckResult[]) => void;
  isLoading?: boolean;
}

export const DuplicateFileModal = ({
  isOpen,
  duplicateFiles,
  onClose,
  onConfirm,
  isLoading = false
}: Props) => {
  // Initialize decisions with default 'reuse' action for all files
  const [decisions, setDecisions] = useState<DuplicateCheckResult[]>([]);

  // Reset decisions when duplicateFiles changes or modal opens
  useEffect(() => {
    if (isOpen && duplicateFiles.length > 0) {
      console.log('[DuplicateModal] Initializing decisions for', duplicateFiles.length, 'files');
      setDecisions(duplicateFiles.map(f => ({ 
        ...f, 
        action: 'reuse' as const, 
        selectedExistingId: f.duplicates[0]?.id 
      })));
    }
  }, [isOpen, duplicateFiles]);

  const handleDecisionChange = (index: number, action: 'reuse' | 'reupload', existingId?: string) => {
    console.log('[DuplicateModal] Decision changed:', { index, action, existingId });
    setDecisions(prev => {
      const updated = prev.map((d, i) => 
        i === index ? { ...d, action, selectedExistingId: existingId } : d
      );
      console.log('[DuplicateModal] Updated decisions:', updated.map(d => ({ filename: d.filename, action: d.action, selectedExistingId: d.selectedExistingId })));
      return updated;
    });
  };

  const handleConfirm = () => {
    console.log('[DuplicateModal] Confirm clicked with decisions:', decisions.map(d => ({ 
      filename: d.filename, 
      action: d.action, 
      selectedExistingId: d.selectedExistingId 
    })));
    onConfirm(decisions);
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      
      if (diffHours < 1) return 'Just now';
      if (diffHours < 24) return `${diffHours}h ago`;
      if (diffDays === 1) return 'Yesterday';
      if (diffDays < 7) return `${diffDays} days ago`;
      
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
      });
    } catch {
      return dateString;
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onDismiss={onClose}
      isBlocking={true}
      containerClassName={styles.modalContainer}
    >
      {/* Header */}
      <div className={styles.modalHeader}>
        <div className={styles.modalTitle}>
          <ClockRegular className={styles.headerIcon} />
          <span>Duplicate Files</span>
        </div>
        <button 
          className={styles.closeButton}
          onClick={onClose}
          aria-label="Close"
        >
          <DismissRegular />
        </button>
      </div>

      {/* Description */}
      <div className={styles.modalDescription}>
        These files were recently uploaded. You can reuse the existing version or upload a fresh copy.
      </div>

      {/* Body */}
      <div className={styles.modalBody}>
        {/* Files List */}
        <div className={styles.filesList}>
          {duplicateFiles.map((duplicateFile, index) => (
            <div key={duplicateFile.filename} className={styles.fileCard}>
              {/* File Header with inline actions */}
              <div className={styles.fileHeader}>
                <div className={styles.fileIconWrapper}>
                  <DocumentRegular className={styles.fileIcon} />
                </div>
                <div className={styles.fileInfo}>
                  <div className={styles.fileName}>
                    {duplicateFile.filename}
                    <span className={styles.readyBadge}>
                      <CheckmarkCircleRegular />
                      Ready
                    </span>
                  </div>
                  <div className={styles.fileSubtitle}>
                    Uploaded {formatDate(duplicateFile.duplicates[0]?.upload_date || '')}
                  </div>
                </div>
              </div>

              {/* Decision Toggle - Icon buttons */}
              <div className={styles.decisionSection}>
                <div className={styles.toggleContainer}>
                  <button
                    className={`${styles.toggleButton} ${
                      decisions[index]?.action === 'reuse' ? styles.toggleButtonActive : ''
                    }`}
                    onClick={() => handleDecisionChange(index, 'reuse', duplicateFile.duplicates[0]?.id)}
                    title="Use Existing"
                    aria-label="Use Existing"
                  >
                    <ArrowSyncRegular className={styles.toggleIcon} />
                  </button>
                  <button
                    className={`${styles.toggleButton} ${
                      decisions[index]?.action === 'reupload' ? styles.toggleButtonActive : ''
                    }`}
                    onClick={() => handleDecisionChange(index, 'reupload')}
                    title="Upload New"
                    aria-label="Upload New"
                  >
                    <ArrowUploadRegular className={styles.toggleIcon} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Footer */}
      <div className={styles.modalFooter}>
        <button 
          className={styles.cancelButton} 
          onClick={onClose} 
          disabled={isLoading}
        >
          Cancel
        </button>
        <button 
          className={styles.confirmButton}
          onClick={handleConfirm} 
          disabled={isLoading}
        >
          {isLoading ? (
            <Spinner size={SpinnerSize.xSmall} />
          ) : (
            <>
              <CheckmarkCircleRegular />
              OK
            </>
          )}
        </button>
      </div>
    </Modal>
  );
};
